import React, { Component } from 'react';
import { WithTranslation, withTranslation, Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';

import Page, { PageButtonLink } from '../../../../components/Page';
import Card from '../../../../components/Card';
import { LoadingOverlay } from '../../../../components/Loading';
import { H1, P } from '../../../../components/Typography';
import { Table, Thead, Tbody, Tr, LinkTr, Th, Td } from '../../../../components/Table/Fake';

import { State as HooksState } from '../reducer';
import Icon from '../../../../components/Icons';
import { colors, distances } from '../../../../styles/constants';
import EmptyState, { EmptyStateIconWrapper } from '../../../../components/EmptyState';
import { ButtonLink } from '../../../../components/Buttons';
import { AccountIdsProps, withAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { EnvironmentBadge } from '../../../../components/EnvironmentBadge';
import HookIcon from '../HookIcon';
import { Subscription } from '../../../../types/webhooks';

interface MatchParams {
    accountId: string;
}

export interface HooksListProps extends WithTranslation, RouteComponentProps<MatchParams>, AccountIdsProps {
    getHooks: (accountIds: string[]) => void;
    hooks: HooksState;
}

const SubscriptionRow = (
    {
        subscription,
        accountId,
    }: {
        subscription: Subscription;
        accountId: string;
    }
) => {
    const { t } = useTranslation();
    return (
        <SubscriptionTr
            key={subscription.id}
            to={`/${accountId}/settings/hooks/${subscription.account_id}/${subscription.id}`}
            className={subscription.deleted_at ? 'deleted' : ''}
        >
            <Td className="edge" />
            <Td className="first-child ellipsis">{subscription.config.url}</Td>
            <Td>
                {subscription.deleted_at ? (
                    <Deleted>{t('settings.hooks.state.deleted')}</Deleted>
                ) : subscription.active ? (
                    <Active>{t('settings.hooks.state.active')}</Active>
                ) : (
                    <InActive>{t('settings.hooks.state.disabled')}</InActive>
                )}
            </Td>
            <Td className="last-child ellipsis">
                <i>{subscription.events.join(' ')}</i>
            </Td>
            <Td className="edge" />
        </SubscriptionTr>
    );
};

const SubcriptionsTable = (
    {
        subscriptions,
        accountId,
        title,
    }: {
        subscriptions: Subscription[];
        accountId: string;
        title: string;
    }
) => {
    const { t } = useTranslation();
    return (
        <MarginTopCard noPadding title={title}>
            <Table className="no-wrap">
                <Thead>
                    <Tr>
                        <Th className="edge" />
                        <Th className="first-child ellipsis">{t('settings.hooks.fields.url')}</Th>
                        <Th>{t('settings.hooks.fields.state')}</Th>
                        <Th className="last-child">{t('settings.hooks.fields.events')}</Th>
                        <Th className="edge" />
                    </Tr>
                </Thead>
                <Tbody>
                    {subscriptions.map((subscription) => (
                        <SubscriptionRow
                            key={subscription.id}
                            subscription={subscription}
                            accountId={accountId}
                        />
                    ))}
                </Tbody>
            </Table>
        </MarginTopCard>
    );
};

class HookList extends Component<HooksListProps> {
    componentDidMount() {
        this.props.getHooks(this.props.accountIds);
    }

    render() {
        const { t } = this.props;
        const { hooks, isLoading } = this.props.hooks;
        const accountId = this.props.match.params.accountId;
        const prodSubscriptions = hooks.filter(x => x.account_id?.startsWith('P'));
        const hasProdSubscriptions = prodSubscriptions.length > 0;
        const testSubscriptions = hooks.filter(x => x.account_id?.startsWith('T'));
        const hasTestSubscriptions = testSubscriptions.length > 0;

        return (
            <Page
                title={
                    <React.Fragment>
                        <Title>{t('settings.hooks.title')}</Title>
                        <PageButtonLink to={`/${accountId}/settings/hooks/new`}>
                            <Icon icon="document" />
                            {t('settings.hooks.create_new_hook')}
                        </PageButtonLink>
                    </React.Fragment>
                }
            >
                {hasProdSubscriptions && (
                    <SubcriptionsTable
                        accountId={accountId}
                        subscriptions={prodSubscriptions}
                        title={t('environments.production')}
                    />
                )}
                {hasTestSubscriptions && (
                    <SubcriptionsTable
                        accountId={accountId}
                        subscriptions={testSubscriptions}
                        title={t('environments.test')}
                    />
                )}
                {!hasProdSubscriptions && !hasTestSubscriptions && (
                    <Card noPadding>
                        <Wrapper>
                            {isLoading && <LoadingOverlay />}
                            {!isLoading && hooks.length === 0 && (
                                <EmptyState>
                                    <EmptyStateIconWrapper>
                                        <HookIcon />
                                    </EmptyStateIconWrapper>

                                    <P>
                                        {t('settings.hooks.empty.message')}
                                        <br />
                                        <br />
                                    </P>
                                    <P>
                                        <Trans i18nKey="settings.hooks.empty.docs">
                                            <a
                                                href="https://docs.dintero.com/docs/webhooks/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {' '}
                                            </a>
                                        </Trans>
                                    </P>
                                    <ButtonLink to={`/${accountId}/settings/hooks/new`}>
                                        {t('settings.hooks.empty.call_to_action')}
                                    </ButtonLink>
                                </EmptyState>
                            )}
                        </Wrapper>
                    </Card>
                )}
            </Page>
        );
    }
}

export default withAccountIds(withTranslation()(HookList));

const Wrapper = styled.div`
    position: relative;
`;

const MarginTopCard = styled(Card)`
    margin-top: ${distances.small};
`;

const Title = styled(H1)`
    display: inline-block;
`;

const SubscriptionTr = styled(LinkTr)`
    &.deleted {
        background-color: ${colors.backgroundAlt};
    }
`;

const Active = styled.span`
    color: ${colors.primary};
`;

const Deleted = styled.span`
    color: ${colors.invalid};
`;

const InActive = Deleted;
